import { canonicalizeRetailMl } from '@/lib/metrics';
import type { Offer, PriceContext, PriceOverride } from '@/types/data';

export interface SelectedPrice {
  packagePriceUsd: number | null;
  containerCount: number;
  containerMl: number;
  label: string;
  provenance: PriceContext | 'unavailable';
  region: string | null;
  retailer: string | null;
  checkedAt: string | null;
  offerId: string | null;
  isUserOverride: boolean;
  notes?: string;
}

function parseInstant(iso: string | null, timeZone: string): number | null {
  if (!iso) return null;
  // Treat date-only as end of day in the stated zone by appending noon UTC
  // when no time is present; callers should use ISO timestamps when possible.
  const hasTime = iso.includes('T');
  const date = new Date(hasTime ? iso : `${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  void timeZone;
  return date.getTime();
}

/** Active sale price if within validity window; otherwise regular price. */
export function resolveOfferPrice(
  offer: Offer,
  now: Date = new Date(),
): number | null {
  const nowMs = now.getTime();
  const from = parseInstant(offer.validFrom, offer.dateTimeZone);
  const through = parseInstant(offer.validThrough, offer.dateTimeZone);

  const saleActive =
    offer.salePrice != null &&
    offer.salePrice > 0 &&
    (from == null || nowMs >= from) &&
    (through == null || nowMs <= through);

  if (saleActive) return offer.salePrice;
  if (offer.regularPrice != null && offer.regularPrice > 0) {
    return offer.regularPrice;
  }
  return null;
}

export function offerLabel(offer: Offer, price: number | null): string {
  if (price == null) return 'Price unavailable';
  if (offer.priceContext === 'abc-reference') {
    return `Virginia ABC reference · ${offer.region}`;
  }
  if (offer.priceContext === 'estimated') {
    return `Estimated retail price · ${offer.region}`;
  }
  if (offer.priceContext === 'user-override') {
    return 'Your price';
  }
  return `${offer.retailer} · ${offer.region}`;
}

export function pickDefaultOffer(offers: Offer[]): Offer | null {
  if (offers.length === 0) return null;
  const defaults = offers.filter((o) => o.isDefault);
  if (defaults.length > 0) return defaults[0]!;
  // Prefer current retail/ABC with a usable price; do not silently pick largest pack.
  const ranked = [...offers].sort((a, b) => {
    const aScore =
      (a.status === 'current' ? 2 : 0) +
      (a.priceContext === 'abc-reference' || a.priceContext === 'retail' ? 1 : 0);
    const bScore =
      (b.status === 'current' ? 2 : 0) +
      (b.priceContext === 'abc-reference' || b.priceContext === 'retail' ? 1 : 0);
    if (bScore !== aScore) return bScore - aScore;
    return a.id.localeCompare(b.id);
  });
  return ranked[0] ?? null;
}

/**
 * Precedence: user override → selected reference offer → regular reference →
 * labeled estimate → unavailable.
 */
export function selectPrice(
  productId: string,
  offers: Offer[],
  override: PriceOverride | undefined,
  selectedOfferId?: string | null,
  now: Date = new Date(),
): SelectedPrice {
  if (override && override.productId === productId) {
    return {
      packagePriceUsd: override.packagePriceUsd,
      containerCount: override.containerCount,
      containerMl: canonicalizeRetailMl(override.containerMl),
      label: 'Your price',
      provenance: 'user-override',
      region: null,
      retailer: null,
      checkedAt: override.updatedAt,
      offerId: null,
      isUserOverride: true,
      notes: override.includeTaxDeposit
        ? 'Includes tax/deposit as entered'
        : undefined,
    };
  }

  const productOffers = offers.filter((o) => o.productId === productId);
  const selected =
    (selectedOfferId
      ? productOffers.find((o) => o.id === selectedOfferId)
      : null) ?? pickDefaultOffer(productOffers);

  if (!selected) {
    return {
      packagePriceUsd: null,
      containerCount: 1,
      containerMl: 0,
      label: 'Price unavailable',
      provenance: 'unavailable',
      region: null,
      retailer: null,
      checkedAt: null,
      offerId: null,
      isUserOverride: false,
    };
  }

  const price = resolveOfferPrice(selected, now);
  return {
    packagePriceUsd: price,
    containerCount: selected.containerCount,
    containerMl: canonicalizeRetailMl(selected.containerMl),
    label: offerLabel(selected, price),
    provenance: price == null ? 'unavailable' : selected.priceContext,
    region: selected.region,
    retailer: selected.retailer,
    checkedAt: selected.checkedAt,
    offerId: selected.id,
    isUserOverride: false,
    notes: selected.notes,
  };
}
